import { isEmpty, isEqual } from 'lodash'
import { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import { Row, Col, Container, Input } from 'reactstrap'
import { dispatchSweetAlert, clearSweetAlert } from '../../actions'
import { addressLabel } from '../../util/utils'
import CheckBox from '../general/CheckBox'
import { clearShippingQuote, setShippingLoading } from './checkoutFunctions'

const CheckoutAddressList = props => {
  const [showMore, setShowMore] = useState(false)
  const [displayedAddresses, setDisplayedAddresses] = useState(3)
  const [searchedAddresses, setSearchAddresses] = useState([])
  const [addressSearch, setAddressSearch] = useState('')
  const [changeAddressConfirm, setChangeAddressConfirm] = useState(false)

  useEffect(() => {
    // if a shipping address is selected,
    // only show the selected address, unless showMore is true
    const shippingAddressIsEmpty = isEmpty(props.selectedCustomerAddress)
    if (props.addressType === 'delivery' && !shippingAddressIsEmpty && displayedAddresses > 1 && !showMore) {
      setDisplayedAddresses(1)
    }
  }, [props.selectedCustomerAddress])

  useEffect(() => {
    // if a billing address is selected,
    // only show the selected address, unless showMore is true
    const billingAddressIsEmpty = isEmpty(props.selectedBillingAddress)
    if (props.addressType === 'billing' && !billingAddressIsEmpty && displayedAddresses > 1 && !showMore) {
      setDisplayedAddresses(1)
    }
  }, [props.selectedBillingAddress])

  useEffect(() => {
    if (isEmpty(props.selectedBillingAddress) && displayedAddresses === 1 && props.addressType === 'billing') {
      setDisplayedAddresses(3)
    }
  }, [props.selectedBillingAddress, props.useShippingAsBilling])
  // if customer address is empty, always show at least three options

  useEffect(() => {
    if (showMore === true) {
      setDisplayedAddresses(props.addresses.length)
    }
  }, [showMore])

  useEffect(() => {
    if (props.addresses) {
      setSearchAddresses(props.addresses)
    }
  }, [props.addresses])

  // for multi-address, when user confirms, then we can set address
  useEffect(() => {
    if (changeAddressConfirm && props.addressType === 'delivery') {
      props.dispatchSweetAlert({
        type: 'info',
        alertMessage: 'Change shipping address for all items in your cart?',
        showCancel: true,
        onConfirm: () => handleSelectAddress(changeAddressConfirm),
        confirmBtnCssClass: 'mf-primary-btn alert-width-btn',
        onCancel: () => props.clearSweetAlert()
      })
    }
  }, [changeAddressConfirm])

  const handleSelectAddress = address => {
    // if multi-address on this portal, make user confirm first,
    // unless no address selections have been made yet
    if (props.portal.multiple_address_shipping && props.addressType !== 'billing' && props.currentUser.id) {
      // only ask for confirmation if there is no address already selected
      if (!changeAddressConfirm && isEmpty(props.selectedCustomerAddress)) {
        // if (!changeAddressConfirm && (addressSelectionHasBeenMade)) {
        setChangeAddressConfirm(address)
        return
      }
    }

    // for customer address list, there are shipping concerns:
    if (props.addressType === 'delivery') {
      props.setShippingLoading(true)
      // clear any shipping quote/reduction, as it may change
      props.clearShippingQuote()
    }

    // if already selected, AND we are clicking on that one, de-select it
    if (!isEmpty(props.selectedAddress) && isEqual(address, props.selectedAddress)) {
      setShowMore(false)
      setDisplayedAddresses(3)
      setSearchAddresses(props.addresses)
      setAddressSearch('')
      props.handleSelectAddress(address, true) // pass true for deselect
    }
    // otherwise, select the address and collapse options
    else {
      // collapse to single address when selection made
      setShowMore(false)
      setDisplayedAddresses(1)
      setSearchAddresses(props.addresses)
      setAddressSearch('')
      props.handleSelectAddress(address)
    }
    // reset confirmation for multi-address
    setChangeAddressConfirm(false)
  }

  // Clickable row displaying address
  const AddressDisplayCard = (address = {}, i) => {
    return (
      <Row
        key={i}
        className={`cart-flex mf-border mf-pt-2 mf-pb-2 ${
          isEqual(props.selectedAddress, address) ? 'alert-success' : 'white-background'
        }`}
        style={{ cursor: 'pointer', borderRadius: '3px' }}
        onClick={() => handleSelectAddress(address)}
      >
        <Col sm={10}>{addressLabel(address)}</Col>

        <Col sm={2} style={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-end' }}>
          <CheckBox checked={isEqual(props.selectedAddress, address)} onChange={() => {}} />
        </Col>
      </Row>
    )
  }

  const handleShowMore = () => {
    if (showMore === false) {
      setDisplayedAddresses(props.addresses.length)
      setShowMore(true)
    } else {
      setDisplayedAddresses(3)
      setShowMore(false)
    }
  }

  const handleSearchAddresses = e => {
    setAddressSearch(e.target.value)
    const searchQuery = e.target.value.toLowerCase()

    let addressesList = props.addresses.map(obj => {
      return Object.keys(obj).reduce((accumulator, key) => {
        if (
          key === 'id' ||
          key === 'address_line_1' ||
          key === 'address_line_2' ||
          key === 'address_phone' ||
          key === 'city' ||
          key === 'country' ||
          key === 'country_code' ||
          key === 'full_name' ||
          key === 'state' ||
          key === 'zip_code' ||
          key === 'company_name'
        ) {
          accumulator[key] = obj[key]
        }
        return accumulator
      }, {})
    })

    if (searchQuery.includes(' ')) {
      searchQuery.split(' ').map(query => {
        addressesList = addressesList.filter(obj =>
          Object.values(obj)
            .filter(val => !!val && typeof val === 'string')
            .some(val => val.toLowerCase().includes(query))
        )
      })
    } else {
      addressesList = addressesList.filter(obj =>
        Object.values(obj)
          .filter(val => !!val && typeof val === 'string')
          .some(val => val.toLowerCase().includes(searchQuery))
      )
    }
    setSearchAddresses(props.addresses.filter(address => addressesList.map(address => address.id).includes(address.id)))
  }

  // If we are in the billing list, and address matches shipping, and both selections are present, render null
  const addressesArePresentAndEqual =
    !isEmpty(props.selectedBillingAddress) &&
    !isEmpty(props.selectedCustomerAddress) &&
    isEqual(props.selectedBillingAddress, props.selectedCustomerAddress)

  if (addressesArePresentAndEqual && props.addressType === 'billing' && !props.allItemsAreDigital) {
    return null
  }

  return (
    <Container>
      {/* Multi-address info text */}
      {props.addressType === 'delivery' &&
      props.portal.multiple_address_shipping &&
      props.currentUser.id &&
      isEmpty(props.selectedCustomerAddress) ? (
        <p style={{ paddingTop: '5px' }}>
          <strong>Multi-Address Shipping Available</strong> - Select default shipping address below, then optionally
          assign additional addresses to each product / product quantity.
        </p>
      ) : null}

      {props.portal.multiple_address_shipping &&
      props.currentUser.id &&
      props.checkout.multiAddressSelection.length > 0 &&
      props.checkout.multiAddressSelection
        .map(sel => sel.address && sel.address.id)
        .filter((id, i, arr) => arr.indexOf(id) === i).length > 1 ? (
        <p style={{ paddingTop: '5px' }}>
          <em>Multiple Addresses selected - See Below</em>
        </p>
      ) : null}

      {/* Selected address goes here, if present, at top of list */}
      {props.selectedAddress && !isEmpty(props.selectedAddress) ? AddressDisplayCard(props.selectedAddress) : null}

      {/* {Search For Addresses Input} */}
      {showMore && displayedAddresses > 1 && props.currentUser.id ? (
        <Row className={props.selectedAddress && !isEmpty(props.selectedAddress) ? null : 'mf-mt-3'}>
          <Col>
            <Input
              placeholder="Search For Addresses"
              onChange={handleSearchAddresses}
              spellCheck="true"
              type="text"
              value={addressSearch}
            />
          </Col>
        </Row>
      ) : null}

      {/* Then map through other address */}
      {props.currentUser.id &&
        searchedAddresses &&
        searchedAddresses.map((address, i) => {
          if (i > displayedAddresses - 1) {
          }
          // if there is a selected address and this is it, don't render it in the list - it is rendered above
          else if (!isEmpty(props.selectedAddress) && isEqual(props.selectedAddress, address)) {
          } else if (displayedAddresses === 1 && !isEmpty(props.selectedAddress)) {
          } else {
            return AddressDisplayCard(address, i)
          }
        })}

      {/* "Show More" button */}
      {!showMore &&
      props.currentUser.id &&
      ((props.addresses && props.addresses.length > 3) || Object.keys(props.selectedAddress) > 0) ? (
        <Row>
          <Col>
            <p className="mf-m-0" style={{ cursor: 'pointer' }} onClick={handleShowMore}>
              Show More...
            </p>
          </Col>
        </Row>
      ) : null}
    </Container>
  )
}

const mapStateToProps = state => {
  return {
    selectedCustomerAddress: state.checkout.selectedCustomerAddress,
    selectedBillingAddress: state.checkout.selectedBillingAddress,
    checkout: state.checkout,
    portal: state.portal,
    currentUser: state.currentUser
  }
}

export default connect(mapStateToProps, {
  clearShippingQuote,
  setShippingLoading,
  dispatchSweetAlert,
  clearSweetAlert
})(CheckoutAddressList)
